import defu from 'defu';

// Every service call in the funnel goes through this client, so the transient-failure policy
// belongs here rather than in each caller.
//
// Why a client timeout exists at all: the backend's own ceiling is a 30s Lambda timeout, so a
// stalled request otherwise strands the buyer on a loading skeleton for half a minute before
// failing. Measured production latency is p50 231ms / p99 3.3s / p99.9 13.9s — an attempt still
// running at 10s has stalled rather than merely being slow, and abandoning it for a fresh attempt
// returns faster than waiting the stall out.
const READ_TIMEOUT_MS = 10_000;
// Writes get a far looser bound. A read cut short costs a replay; a write cut short while the
// backend is still charging the card shows the buyer a failure for a purchase that actually went
// through. This clock starts in the browser, so it has to cover everything that happens before the
// backend's own 30s budget even begins — DNS, TLS, gateway queueing, a cold start — which is why
// the margin is 30s and not the couple of seconds the backend ceiling alone would suggest. It is a
// hang-catcher of last resort, not a latency budget.
const WRITE_TIMEOUT_MS = 60_000;
// Wall-clock ceiling across all attempts, so a bad minute upstream can't strand the buyer.
const RETRY_BUDGET_MS = 20_000;
// Below this, a fresh attempt is not worth starting: it would almost certainly be cut off by the
// budget rather than answered. p50 is 231ms, so a second is still a genuine chance.
const MIN_ATTEMPT_MS = 1_000;
const MAX_RETRIES = 2;
const BASE_BACKOFF_MS = 300;
// Worth a second attempt: the request never got a verdict, or the server explicitly said "later".
// Everything else (400/403/404/409/422) is a real answer, and repeating it only delays the truth.
const RETRYABLE_STATUSES = new Set([408, 425, 429, 500, 502, 503, 504]);

// ofetch wraps every failure — including its own timeout abort — in a FetchError carrying `request`;
// `response` is present only when the server actually answered. An error without `request` came from
// our own code (a throwing token refresh, say) rather than the wire, so replaying it is pointless.
function isTransient(err: any): boolean {
    if (!err?.request) return false;
    if (!err.response) return true;
    return RETRYABLE_STATUSES.has(err.response.status);
}

// Full jitter, because the page fires several GETs at mount: without it their retries synchronise
// into a second burst against a backend that is already struggling.
function backoffMs(attempt: number): number {
    return Math.random() * BASE_BACKOFF_MS * 2 ** attempt;
}

// A server that says "later" picks when. Seconds form only (our API does not send the HTTP-date
// form), and never longer than the budget left.
function retryAfterMs(err: any, remainingMs: number): number | null {
    const header = err?.response?.headers?.get?.('retry-after');
    if (!header) return null;
    const seconds = Number(header);
    if (!Number.isFinite(seconds) || seconds < 0) return null;
    return Math.min(seconds * 1000, remainingMs);
}

export async function usePbApi<T>(url: string, options: any = {}) {
    const auth = useAuthStore();
    const config = useRuntimeConfig();

    // Only GETs may be replayed. Some state-changing calls do carry an Idempotency-Key the backend
    // dedupes on, but replaying them is the caller's decision to make, not this client's.
    const replayable = String(options.method ?? 'GET').toUpperCase() === 'GET';

    const defaults = {
        baseURL: config.public.pb.apiDomain,
        timeout: replayable ? READ_TIMEOUT_MS : WRITE_TIMEOUT_MS,
        headers: {
            Accept: 'application/json',
        },
    };

    const params = defu(options, defaults);

    let status: number = 200;
    let message: string = '';
    let data: T | null = null;
    let refreshAttempted = false;

    async function makeRequest(timeout: number): Promise<void> {
        // Get a valid token (handles refresh and queueing automatically)
        const token = await auth.getValidTokenForRequest();

        // Set Authorization header if we have a token
        if (token) {
            params.headers.Authorization = token;
        }

        const cfkVid = useCookie('cfk_vid');
        if (cfkVid.value) {
            params.headers['x-pb-cfkvid'] = cfkVid.value;
        }

        const result = await $fetch<T>(url, {
            ...params,
            timeout,
            // Retries are governed below, not by ofetch, so the 401 refresh stays single-shot.
            retry: 0,
            onResponse({ response }) {
                status = response.status;
            },
        });
        data = result as T;
    }

    function describe(err: any): string {
        if (Array.isArray(err?.response?._data?.errors)) {
            return err.response._data.errors[0]?.message || 'An error occurred';
        }
        if (err?.response?._data?.message) {
            return err.response._data.message;
        }
        return err?.message || err?.statusText || 'An error occurred';
    }

    // A caller that asks for a longer timeout is making a statement about that endpoint, so the
    // budget stretches to hold one full attempt rather than quietly cutting the request short.
    const deadline = Date.now() + Math.max(RETRY_BUDGET_MS, params.timeout);
    // A replayable attempt may not outlive the shared budget: gating only whether a retry *starts*
    // would let a late attempt run its full timeout well past the deadline.
    // Never below MIN_ATTEMPT_MS: a slow token refresh can eat the budget, and an attempt with a
    // zero timeout would abort on arrival rather than fail honestly.
    const attemptTimeout = () => (replayable
        ? Math.max(MIN_ATTEMPT_MS, Math.min(params.timeout, deadline - Date.now()))
        : params.timeout);

    for (let attempt = 0; ; attempt++) {
        try {
            await makeRequest(attemptTimeout());
            return { data: data as T, status, message };
        } catch (err: any) {
            status = err.response?.status || 500;
            data = (err.response?._data || null) as T;

            // Expired access token: refresh once per call — never once per attempt — then fall back
            // into the loop so the replay gets the same transient-failure protection as any other
            // attempt. Replaying inline here instead would leave the expired-token path, which is
            // the common one for a returning buyer, as the only one with no retry at all.
            if (status === 401 && !refreshAttempted) {
                refreshAttempted = true;

                let refreshed = false;
                try {
                    // Attempt to refresh token (this handles queuing internally to prevent race conditions)
                    refreshed = await auth.refreshAccessToken();
                } catch (refreshErr: any) {
                    // Refresh threw an error - just return the error, no redirect needed
                    // (auth store already handles redirects)
                    status = refreshErr.response?.status || 500;
                    message = describe(refreshErr);
                    return { data: data as T, status, message };
                }

                if (!refreshed) {
                    // Refresh failed - auth store has already redirected to login
                    message = 'Session expired. Please log in again.';
                    return { data: data as T, status, message };
                }

                continue;
            }

            const remaining = deadline - Date.now();
            if (replayable && attempt < MAX_RETRIES && isTransient(err)) {
                const wait = retryAfterMs(err, remaining) ?? backoffMs(attempt);
                if (remaining - wait >= MIN_ATTEMPT_MS) {
                    await new Promise(resolve => setTimeout(resolve, wait));
                    continue;
                }
            }

            message = describe(err);
            return { data: data as T, status, message };
        }
    }
}
